// Setting up a Zebra printer from the browser, over Bluetooth, standing next
// to it. The printer exposes one Bluetooth service that takes the same
// commands as its other ports (Zebra's Bluetooth LE application note): bytes
// written to one characteristic go to the printer, its answers come back on
// the other. Only Chrome and Edge let a page use Bluetooth.
const SERVICE = '38eb4a80-c570-11e3-9507-0002a5d5c51b';
const FROM_PRINTER = '38eb4a81-c570-11e3-9507-0002a5d5c51b';
const TO_PRINTER = '38eb4a82-c570-11e3-9507-0002a5d5c51b';
// The printer takes at most 512 bytes in one write; smaller pieces also pass
// through phones that negotiate a short packet size.
const CHUNK_BYTES = 128;
const ANSWER_TIMEOUT_MS = 5000;

const encoder = new TextEncoder();
const two = value => String(value).padStart(2, '0');

export const bluetoothAvailable = () => typeof navigator !== 'undefined'
  && Boolean(navigator.bluetooth && typeof navigator.bluetooth.requestDevice === 'function');

// A network name the printer's settings can carry: it is written between
// double quotes, at most 32 bytes.
export const networkNameFits = name => typeof name === 'string' && name.length > 0
  && encoder.encode(name).length <= 32 && !/["\r\n]/.test(name);
// WPA2 passwords are 8 to 63 characters.
export const passwordFits = password => typeof password === 'string' && password.length >= 8 && password.length <= 63;

// The 256-bit key a WPA2 network really uses, derived from its name and
// password the standard way (PBKDF2, HMAC-SHA1, 4096 rounds). The printer is
// given this key, and it is what XOmics keeps: the password itself never
// leaves this page.
export async function wifiKey(networkName, password) {
  const material = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-1', salt: encoder.encode(networkName), iterations: 4096 }, material, 256);
  return Array.from(new Uint8Array(bits), byte => byte.toString(16).padStart(2, '0')).join('');
}

const setting = (name, value) => encoder.encode(`! U1 setvar "${name}" "${value}"\r\n`);

// Everything the printer has to be told, in the order it needs it: the roots
// it must trust for our address, its clock (certificates are checked against
// it), the Wi-Fi, where to connect, and for a shipping printer that labels
// arrive as PDF. The restart at the end makes it join the Wi-Fi and dial in.
export function setupCommands({ networkName, key, address, certificates, purpose, now = new Date() }) {
  const roots = encoder.encode(certificates);
  const head = encoder.encode(`~DYE:WEBLINK1_CA,B,NRD,${roots.length},,`);
  const file = new Uint8Array(head.length + roots.length);
  file.set(head); file.set(roots, head.length);
  return [
    file,
    setting('rtc.date', `${two(now.getUTCMonth() + 1)}-${two(now.getUTCDate())}-${now.getUTCFullYear()}`),
    setting('rtc.time', `${two(now.getUTCHours())}:${two(now.getUTCMinutes())}:${two(now.getUTCSeconds())}`),
    setting('wlan.essid', networkName),
    setting('wlan.security', 'wpa psk'),
    setting('wlan.wpa.psk', key),
    setting('weblink.ip.conn1.location', address),
    ...(purpose === 'shipping_labels' ? [setting('apl.enable', 'pdf')] : []),
    setting('device.reset', ''),
  ];
}

// Opens the browser's own device picker, limited to Zebra printers, and
// returns a line to the chosen one.
export async function connectPrinter() {
  const device = await navigator.bluetooth.requestDevice({ filters: [{ services: [SERVICE] }] });
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(SERVICE);
  const toPrinter = await service.getCharacteristic(TO_PRINTER);
  const fromPrinter = await service.getCharacteristic(FROM_PRINTER);
  const decoder = new TextDecoder();
  let heard = '';
  let waiter = null;
  fromPrinter.addEventListener('characteristicvaluechanged', (event) => {
    heard += decoder.decode(event.target.value, { stream: true });
    if (waiter) waiter();
  });
  await fromPrinter.startNotifications();

  async function send(bytes) {
    for (let offset = 0; offset < bytes.length; offset += CHUNK_BYTES) {
      await toPrinter.writeValueWithResponse(bytes.slice(offset, offset + CHUNK_BYTES));
    }
  }
  // One printer setting, read back: the answer comes between double quotes.
  function ask(name) {
    heard = '';
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => { waiter = null; reject(new Error('PRINTER_SILENT')); }, ANSWER_TIMEOUT_MS);
      waiter = () => {
        const answer = /"([^"]*)"/.exec(heard);
        if (!answer) return;
        window.clearTimeout(timer); waiter = null; resolve(answer[1]);
      };
      send(encoder.encode(`! U1 getvar "${name}"\r\n`)).catch((error) => { window.clearTimeout(timer); waiter = null; reject(error); });
    });
  }
  return {
    name: device.name || '',
    send,
    ask,
    disconnect() { if (device.gatt.connected) device.gatt.disconnect(); },
  };
}
